import React, { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import "./UpcomingEvents.css";

const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;

const getStartOfDay = (date) => {
  const startOfDay = new Date(date);
  startOfDay.setHours(0, 0, 0, 0);
  return startOfDay;
};

const getEndOfDay = (date) => {
  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);
  return endOfDay;
};

const formatEventDate = (dateString) => {
  const date = new Date(dateString);
  const formattedDate = new Intl.DateTimeFormat("de-DE", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
  const formattedTime = new Intl.DateTimeFormat("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);

  return `${formattedDate} · ${formattedTime} Uhr`;
};

const getRegistrationHref = (phoneNumber) =>
  `tel:${phoneNumber.replace(/[^+\d]/g, "")}`;

const UpcomingEvents = () => {
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [daysRemaining, setDaysRemaining] = useState(0);
  const [status, setStatus] = useState("loading");

  const nextEvent = upcomingEvents[0] ?? null;

  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-in-out",
      once: true,
    });

    const controller = new AbortController();

    const fetchEvents = async () => {
      try {
        const response = await fetch("/data/events.json", {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const eventsData = await response.json();

        if (!Array.isArray(eventsData)) {
          throw new Error("Das Event-Datenformat ist ungültig.");
        }

        const now = new Date();
        const futureEvents = eventsData
          .filter((event) => {
            const eventDate = new Date(event.date);
            return (
              event.title &&
              !Number.isNaN(eventDate.getTime()) &&
              getEndOfDay(eventDate) >= now
            );
          })
          .sort((a, b) => new Date(a.date) - new Date(b.date));

        setUpcomingEvents(futureEvents);
        setStatus("success");
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Fehler beim Laden der Events:", error);
          setStatus("error");
        }
      }
    };

    fetchEvents();

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!nextEvent) return undefined;

    const calculateDaysRemaining = () => {
      const eventDay = getStartOfDay(new Date(nextEvent.date));
      const today = getStartOfDay(new Date());
      const difference = eventDay.getTime() - today.getTime();

      setDaysRemaining(Math.max(0, Math.round(difference / MILLISECONDS_PER_DAY)));
    };

    calculateDaysRemaining();
    const interval = window.setInterval(calculateDaysRemaining, 1000 * 60);

    return () => window.clearInterval(interval);
  }, [nextEvent]);

  if (status === "loading") {
    return (
      <div className="next-event-card next-event-card--status" aria-live="polite">
        <span className="next-event-badge">Nächstes Event</span>
        <p className="event-status-message">Veranstaltungen werden geladen …</p>
      </div>
    );
  }

  if (status === "error" || !nextEvent) {
    return (
      <div
        className="next-event-card next-event-card--status"
        data-aos="fade-left"
        data-aos-delay="200"
      >
        <span className="next-event-badge">Nächstes Event</span>
        <p className="event-status-message">
          {status === "error"
            ? "Die Veranstaltungen konnten gerade nicht geladen werden."
            : "Aktuell sind keine Veranstaltungen geplant."}
        </p>
      </div>
    );
  }

  const countdownLabel =
    daysRemaining === 0
      ? "Heute"
      : `Noch ${daysRemaining} ${daysRemaining === 1 ? "Tag" : "Tage"}`;

  return (
    <article
      className={`next-event-card${nextEvent.imageUrl ? "" : " next-event-card--without-image"}`}
      data-aos="fade-left"
      data-aos-delay="200"
    >
      {nextEvent.imageUrl && (
        <div className="event-image-wrapper">
          <img
            src={nextEvent.imageUrl}
            alt={nextEvent.imageAlt || `Impression zum Event ${nextEvent.title}`}
            className="event-image"
          />
        </div>
      )}

      <div className="event-card-content">
        <div className="event-card-topline">
          <span className="next-event-badge">Nächstes Event</span>
          <span className="event-countdown">{countdownLabel}</span>
        </div>

        <h2 className="event-title-card">{nextEvent.title}</h2>

        <div className="event-meta">
          <time dateTime={nextEvent.date} className="event-meta-item">
            <span className="event-meta-label">Termin</span>
            {formatEventDate(nextEvent.date)}
          </time>

          {nextEvent.location && (
            <p className="event-meta-item">
              <span className="event-meta-label">Ort</span>
              {nextEvent.location}
            </p>
          )}
        </div>

        {nextEvent.details && (
          <p className="event-description">{nextEvent.details}</p>
        )}

        <div className="event-actions">
          {nextEvent.registrationPhone && (
            <a
              className="event-registration-button"
              href={getRegistrationHref(nextEvent.registrationPhone)}
              aria-label={`${nextEvent.registrationLabel || "Jetzt anmelden"}: ${nextEvent.registrationPhone}`}
            >
              {nextEvent.registrationLabel || "Jetzt anmelden"}
            </a>
          )}

          <details className="event-overview">
            <summary>Alle Veranstaltungen</summary>
            <ul className="event-overview-list">
              {upcomingEvents.map((event) => (
                <li key={`${event.date}-${event.title}`}>
                  <span>{event.title}</span>
                  <time dateTime={event.date}>{formatEventDate(event.date)}</time>
                </li>
              ))}
            </ul>
          </details>
        </div>
      </div>
    </article>
  );
};

export default UpcomingEvents;
